"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { useState } from "react";
import React from "react";

interface SpecialisationMetadata {
  id: string;
  card_title: string;
  card_description: string;
  detail_title?: string;
  detail_subtitle?: string;
  detail_description?: string;
  icon?: string;
  hover_icon?: string;
  priority?: number;
  meta_status?: boolean;
  // Mobile Variant 2 (accordion) styling
  mobile_v2_card_color?: string;
  mobile_v2_card_gradient?: string;
  mobile_v2_title_text_color?: string;
  mobile_v2_sub_title_text_color?: string;
  mobile_v2_description_text_color?: string;
  mobile_v2_accordion_element_color?: string;
  // Mobile Variant 2 gradient fields
  mobile_v2_gradient_angle?: string;
  mobile_v2_gradient_start_color?: string;
  mobile_v2_gradient_end_color?: string;
}

interface CourseSpecialisationsMobileProps {
  title: string;
  titleTextColor: string;
  backgroundColor: string;
  displayData: SpecialisationMetadata[];
  // Global navigation icons (from parent)
  topArrowIcon?: string;
  topArrowIconAlt?: string;
  bottomArrowIcon?: string;
  bottomArrowIconAlt?: string;
  onCardToggle?: (
    index: number,
    item: SpecialisationMetadata,
    isExpanded: boolean
  ) => void;
}

const CourseSpecialisationsMobile = React.forwardRef<
  HTMLElement,
  CourseSpecialisationsMobileProps
>(
  (
    {
      title,
      titleTextColor,
      backgroundColor,
      displayData,
      topArrowIcon,
      topArrowIconAlt = "Collapse",
      bottomArrowIcon,
      bottomArrowIconAlt = "Expand",
      onCardToggle,
    },
    ref
  ) => {
    const [expandedIndex, setExpandedIndex] = useState<number>(0);

    const handleCardClick = (index: number) => {
      const isExpanded = expandedIndex === index;
      const newExpandedIndex = isExpanded ? -1 : index;
      setExpandedIndex(newExpandedIndex);
      const item = displayData[index];
      if (item) {
        onCardToggle?.(index, item, !isExpanded);
      }
    };

    return (
      <section
        ref={ref}
        className={cn(
          "flex flex-col items-center justify-center w-full",
          "px-5 py-8"
        )}
        style={{ backgroundColor }}
      >
        {/* Title */}
        <h2
          className={cn("text-heading-6-semibold", "mb-6 text-center")}
          style={{ color: titleTextColor }}
        >
          {title}
        </h2>

        {/* Accordion Cards */}
        <div className="flex flex-col gap-2 w-full max-w-[312px]">
          {displayData.map((item, index) => {
            const isExpanded = expandedIndex === index;

            // Per-item colors
            const cardColor = item.mobile_v2_card_color || "#0A3D5B";
            // Build gradient from individual fields if available, else use pre-built gradient
            const cardGradient =
              item.mobile_v2_gradient_start_color &&
              item.mobile_v2_gradient_end_color
                ? `linear-gradient(${
                    item.mobile_v2_gradient_angle || "0deg"
                  }, ${item.mobile_v2_gradient_start_color} 0%, ${
                    item.mobile_v2_gradient_end_color
                  } 100%)`
                : item.mobile_v2_card_gradient ||
                  "linear-gradient(0deg, #082F47 0%, #0E5479 50%, #2375A8 100%)";
            const titleColor = item.mobile_v2_title_text_color || "#FFFFFF";
            const subTitleColor =
              item.mobile_v2_sub_title_text_color || "#FFFFFF";
            const descriptionColor =
              item.mobile_v2_description_text_color || "#BCD0E0";
            const accordionElementColor =
              item.mobile_v2_accordion_element_color || "#BCD0E0";

            return (
              <div key={item.id} className="w-full">
                {isExpanded ? (
                  /* Expanded Card */
                  <div className="flex flex-col items-start w-full">
                    {/* Expanded Header with Gradient */}
                    <div
                      className={cn(
                        "flex flex-col items-center justify-center p-4",
                        "rounded-t-[6px] w-full"
                      )}
                      style={{
                        backgroundImage: cardGradient,
                      }}
                    >
                      <div className="flex flex-col gap-2 items-center justify-center w-full">
                        {/* Title & Subtitle */}
                        <div className="flex flex-col items-start w-full leading-normal">
                          <p
                            className="text-heading-6-bold w-full line-clamp-3"
                            style={{ color: titleColor }}
                          >
                            {item.detail_title || item.card_title}
                          </p>
                          <p
                            className="text-heading-9-semibold w-full"
                            style={{ color: subTitleColor }}
                          >
                            {item.detail_subtitle || item.card_description}
                          </p>
                        </div>

                        {/* Divider Line */}
                        <div
                          className="w-full h-[1px] opacity-50 "
                          style={{ backgroundColor: accordionElementColor }}
                        />

                        <p
                          className="text-body-4-regular w-full"
                          style={{ color: descriptionColor }}
                        >
                          {item.detail_description || item.card_description}
                        </p>
                      </div>
                    </div>

                    {/* Collapse Button */}
                    <button
                      onClick={() => handleCardClick(index)}
                      className={cn(
                        "w-full h-8 flex items-center justify-center",
                        "rounded-b-[6px] cursor-pointer"
                      )}
                      style={{
                        backgroundColor: cardColor,
                        borderColor: accordionElementColor,
                      }}
                    >
                      {topArrowIcon ? (
                        <Image
                          src={topArrowIcon}
                          alt={topArrowIconAlt}
                          width={24}
                          height={24}
                          className="object-contain"
                        />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="rotate-180"
                        >
                          <path
                            d="M7 10L12 15L17 10"
                            stroke={accordionElementColor}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                ) : (
                  /* Collapsed Card */
                  <div
                    className={cn(
                      "flex flex-col gap-3 items-center justify-center",
                      "pt-2 rounded-[6px] border cursor-pointer"
                    )}
                    style={{
                      backgroundColor: cardColor,
                      borderColor: accordionElementColor,
                    }}
                    onClick={() => handleCardClick(index)}
                  >
                    {/* Card Content */}
                    <div className="flex flex-col items-center justify-center px-4 w-full leading-normal">
                      <p
                        className="text-heading-8-semibold w-full"
                        style={{ color: titleColor }}
                      >
                        {item.card_title}
                      </p>
                      <p
                        className="text-body-4-regular w-full"
                        style={{ color: subTitleColor }}
                      >
                        {item.card_description}
                      </p>
                    </div>

                    {/* Expand Button */}
                    <div
                      className="w-full h-8 flex items-center justify-center"
                      style={{ backgroundColor: accordionElementColor }}
                    >
                      {bottomArrowIcon ? (
                        <Image
                          src={bottomArrowIcon}
                          alt={bottomArrowIconAlt}
                          width={24}
                          height={24}
                          className="object-contain"
                        />
                      ) : (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M7 10L12 15L17 10"
                            stroke={cardColor}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  }
);

CourseSpecialisationsMobile.displayName = "CourseSpecialisationsMobile";

export default CourseSpecialisationsMobile;
